import React from 'react'
import { motion } from 'framer-motion'
import { StaticImage } from 'gatsby-plugin-image'
import { FiAward, FiEdit, FiDownload, FiCheckCircle, FiTarget, FiTrendingUp, FiShield, FiCpu, FiZap, FiBriefcase } from 'react-icons/fi'

const CoverLetterBuilder = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  }

  const staggerChildren = {
    animate: { transition: { staggerChildren: 0.2 } }
  }

  const aiFeatures = [
    { icon: <FiCpu className="w-6 h-6" />, title: "AI Writing Assistant", description: "Generate compelling content instantly" },
    { icon: <FiZap className="w-6 h-6" />, title: "Smart Personalization", description: "Auto-match job requirements" },
    { icon: <FiBriefcase className="w-6 h-6" />, title: "Industry Insights", description: "Role-specific suggestions" }
  ]

  const features = [
    { icon: <FiAward className="w-6 h-6" />, title: "Perfect Match", description: "AI analyzes job descriptions" },
    { icon: <FiEdit className="w-6 h-6" />, title: "Real-time Enhancement", description: "Smart writing suggestions" },
    { icon: <FiTarget className="w-6 h-6" />, title: "Keywords Optimization", description: "Industry-specific terms" },
    { icon: <FiShield className="w-6 h-6" />, title: "ATS-Optimized", description: "Guaranteed system pass" }
  ]

  const floatingElements = [
    { text: "AI Personalization", className: "top-0 -left-16" },
    { text: "Job Description Match", className: "top-1/4 -right-20" },
    { text: "Industry Keywords", className: "bottom-1/4 -left-16" },
    { text: "Tone Optimization", className: "bottom-0 -right-20" }
  ]
  
  return (
    <div className="relative py-24 bg-white overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-indigo-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial="initial" animate="animate" variants={staggerChildren} className="space-y-12">
          <motion.div variants={fadeInUp} className="text-center">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              <FiCpu className="mr-2" /> AI-Powered Cover Letter Builder
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold text-neutral-900 leading-tight">
              Create a Winning <span className="text-indigo-600">Cover Letter</span> in Minutes
            </h1>
            <p className="mt-6 text-sm md:text-md text-neutral-700 max-w-2xl mx-auto">
              Our AI technology crafts personalized, compelling cover letters that match job requirements and showcase your unique value proposition.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {aiFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-indigo-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex-shrink-0 text-indigo-600 mb-4">{feature.icon}</div>
                <h3 className="text-sm font-semibold text-neutral-900">{feature.title}</h3>
                <p className="text-sm text-neutral-600">{feature.description}</p>
              </div>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp} className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-full md:w-1/2 relative">
            <div className="relative">
                <StaticImage
                  src="../images/templates/cover/minimal.png"
                  alt="AI-Powered Cover Letter"
                  placeholder="blurred"
                  layout="constrained"
                  className="rounded-lg shadow-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 to-transparent rounded-lg"></div>
              </div>
              {floatingElements.map((element, index) => (
                <motion.div
                  key={index}
                  className={`hidden md:flex absolute ${element.className} items-center gap-2`}
                  initial={{ opacity: 0, x: element.className.includes('left') ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                >
                  <span className="bg-white px-4 py-2 rounded-full text-xs font-medium text-indigo-600 shadow-lg border border-indigo-100 whitespace-nowrap">
                    {element.text}
                  </span>
                  <div className="w-8 h-0.5 bg-indigo-200"></div>
                </motion.div>
              ))}
              
              <motion.div 
                className="absolute -top-4 -right-4 bg-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 }}
              >
                AI Powered
              </motion.div>
              
              <motion.div 
                className="absolute z-100 -bottom-4 -left-4 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.7 }}
              >
                95% Success Rate
              </motion.div>
            </div>

            <div className="w-full md:w-1/2 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start p-4 bg-indigo-50 rounded-lg">
                    <div className="flex-shrink-0 text-indigo-600">{feature.icon}</div>
                    <div className="ml-4">
                      <h3 className="text-sm font-semibold text-neutral-900">{feature.title}</h3>
                      <p className="text-sm text-neutral-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="text-center mt-12 space-y-6 bg-gradient-to-b from-white to-indigo-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-neutral-900">Transform Your Job Application Today</h3>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-neutral-600">
              <span className="flex items-center"><FiCheckCircle className="mr-2 text-indigo-600" />2-Minute Generation</span>
              <span className="flex items-center"><FiCheckCircle className="mr-2 text-indigo-600" />AI-Powered Writing</span>
              <span className="flex items-center"><FiCheckCircle className="mr-2 text-indigo-600" />100% ATS-Friendly</span>
            </div>
            <button className="bg-indigo-600 text-white px-8 py-4 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              onClick={() => window.open("https://app.resumeflex.com/cover-letter", "_blank")}>
              Generate Your AI Cover Letter Now
            </button>
            <p className="text-xs text-neutral-500">One-time Payment - Unlimited AI-Powered Cover Letters</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default CoverLetterBuilder
